'use client';

import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { Coin } from '@/lib/api/transaction/models';
import { useIsBalanceHidden } from '@/lib/hooks/useIsBalanceHidden';
import { usePathname } from '@/lib/routing';
import { type BalanceDisplayMode, useBalanceDisplayStore } from '@/lib/store/balance-display-store';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { UnderlineTabs } from '@/lib/ui-kit-v2/underline-tabs';
import { getZoneFromPath } from '@/lib/utils/zones';
import { BalanceCard, type BalanceView } from './BalanceCard';
import { CoinsList } from './CoinsList';
import { CryptoTradingSummary } from './CryptoTradingSummary';
import type { CurrencySelectProps } from './CurrencySelect';
import { DisplaySettings } from './DisplaySettings';
import { PredictionMarketSummary } from './PredictionMarketSummary';
import { type BalancePopoverData, useBalancePopoverData } from './useBalancePopoverData';

/** Which balance the hero shows: every account, or the page's product. */
type Panel = 'assets' | 'prediction' | 'crypto';
type Tab = 'coins' | 'prediction' | 'crypto';

interface BalancePopoverContentProps {
  onDeposit: () => void;
  onConvert: () => void;
  renderAccountLink: (href: string, label: ReactNode) => ReactNode;
  /** Settle coin of the Crypto Trading balance (the trade zone passes the pair's). */
  balanceCurrency?: string;
}

const WALLET_HREF = '/account?tab=wallet';
/** Prediction Markets settle in USDC — also the hero's fallback coin. */
const DEFAULT_CURRENCY = 'USDC';
/** Futures settle coins the Crypto Trading hero can be shown in. */
const CRYPTO_CURRENCIES = ['USDT', 'USDC'];
const DEFAULT_FRACTION_DIGITS = 3;
const DEFAULT_TAB: Record<Panel, Tab> = {
  assets: 'coins',
  prediction: 'prediction',
  crypto: 'crypto',
};
// Figma: 12px under the tabs on Coins (15073:84519), 8px on the summaries (84567 / 84609).
const TAB_BODY_GAP: Record<Tab, string> = {
  coins: 'gap-3',
  prediction: 'gap-2',
  crypto: 'gap-2',
};
const TITLE_KEY: Record<Panel, 'totalAssets' | 'titlePrediction' | 'titleCrypto'> = {
  assets: 'totalAssets',
  prediction: 'titlePrediction',
  crypto: 'titleCrypto',
};

// Both screens share one grid cell, so the panel keeps the balance screen's
// height and the switch is a cross-fade with a short slide (the nav's curve).
const SCREEN_CLASS =
  'col-start-1 row-start-1 transition-[opacity,translate] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';
const SCREEN_HIDDEN_CLASS = 'pointer-events-none opacity-0';

function panelFor(mode: BalanceDisplayMode, isTradeZone: boolean): Panel {
  if (mode === 'total') return 'assets';
  return isTradeZone ? 'crypto' : 'prediction';
}

interface Headline {
  amount: number;
  usdValue: number;
  currencyLabel: string;
  fractionDigits: number;
}

/** The hero figure: Total Assets in the picked coin, else the product's own balance. */
function headlineFor(
  panel: Panel,
  view: BalanceView,
  data: BalancePopoverData,
  displayCoin: Coin | undefined,
): Headline {
  if (panel === 'assets') {
    const usd = view === 'available' ? data.availableAssetsUSD : data.totalAssetsUSD;
    const rate = displayCoin?.usdRate ?? 1;
    return {
      amount: rate > 0 ? usd / rate : usd,
      usdValue: usd,
      currencyLabel: displayCoin?.shortName ?? DEFAULT_CURRENCY,
      fractionDigits: displayCoin?.decimalPlacesShort ?? DEFAULT_FRACTION_DIGITS,
    };
  }
  const amount = view === 'available' ? data.availableTrading : data.totalTrading;
  return {
    amount,
    usdValue: amount * data.tradingRate,
    currencyLabel: data.tradingCoin?.shortName ?? DEFAULT_CURRENCY,
    fractionDigits: data.tradingCoin?.decimalPlacesShort ?? DEFAULT_FRACTION_DIGITS,
  };
}

/** The wallet popover (Figma 15073:84507): hero, Deposit / Convert, per-product tabs. */
export function BalancePopoverContent({
  onDeposit,
  onConvert,
  renderAccountLink,
  balanceCurrency = DEFAULT_CURRENCY,
}: BalancePopoverContentProps) {
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const wasSettingsOpenRef = useRef(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [view, setView] = useState<BalanceView>('total');
  const mode = useBalanceDisplayStore((s) => s.mode);
  const assetsCoinId = useBalanceDisplayStore((s) => s.assetsCoinId);
  const cryptoCurrency = useBalanceDisplayStore((s) => s.cryptoCurrency);
  const setAssetsCoinId = useBalanceDisplayStore((s) => s.setAssetsCoinId);
  const setCryptoCurrency = useBalanceDisplayStore((s) => s.setCryptoCurrency);
  const isTradeZone = getZoneFromPath(usePathname()) === 'trade';
  const panel = panelFor(mode, isTradeZone);
  // Seeded from the layout above; reset when the player comes back from settings.
  const [tab, setTab] = useState<Tab>(DEFAULT_TAB[panel]);
  const t = useTranslations('balancePopover');
  // Crypto Trading: the coin the player picked, else the page pair's settle coin.
  const data = useBalancePopoverData(
    panel === 'crypto' ? (cryptoCurrency ?? balanceCurrency) : DEFAULT_CURRENCY,
  );
  const hidden = useIsBalanceHidden();

  // Back goes inert with its screen: once the balance screen is live again,
  // focus returns to the gear that opened the settings.
  useEffect(() => {
    if (wasSettingsOpenRef.current && !isSettingsOpen) {
      settingsButtonRef.current?.focus({ preventScroll: true });
    }
    wasSettingsOpenRef.current = isSettingsOpen;
  }, [isSettingsOpen]);

  const displayCoin =
    data.coins.find((c) => c.id === (assetsCoinId ?? data.tradingCoin?.id)) ?? data.tradingCoin;
  const headline = headlineFor(panel, view, data, displayCoin);
  // The pair's own settle coin stays pickable too (inverse pairs settle in the base).
  const cryptoSymbols = new Set([...CRYPTO_CURRENCIES, balanceCurrency.toUpperCase()]);
  const cryptoCoins = data.coins.filter((c) => cryptoSymbols.has(c.shortName.toUpperCase()));

  const handleSelectAssetsCoin = (coin: Coin) => setAssetsCoinId(coin.id);
  const handleSelectCryptoCoin = (coin: Coin) => setCryptoCurrency(coin.shortName.toUpperCase());
  // Figma: the hero's "USDC ▾" on Total Assets, "USDT ▾" on Crypto Trading (15073:84596).
  const currencySelects: Record<Panel, CurrencySelectProps | undefined> = {
    assets: { coins: data.coins, selectedId: displayCoin?.id, onSelect: handleSelectAssetsCoin },
    prediction: undefined,
    crypto: {
      coins: cryptoCoins,
      selectedId: data.tradingCoin?.id,
      onSelect: handleSelectCryptoCoin,
    },
  };
  const handleOpenSettings = () => setIsSettingsOpen(true);
  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
    setTab(DEFAULT_TAB[panel]);
  };
  const handleTabChange = (id: string) => setTab(id as Tab);

  const tabs = [
    { id: 'coins', label: t('tabCoins') },
    { id: 'prediction', label: t('tabPrediction') },
    { id: 'crypto', label: t('tabCrypto') },
  ];
  const tabContent: Record<Tab, ReactNode> = {
    coins: <CoinsList coins={data.coins} hidden={hidden} />,
    prediction: (
      <PredictionMarketSummary
        openPositions={data.openPositions}
        openOrders={data.openOrders}
        positionValue={data.positionValue}
        pnl={data.unrealizedPnl}
        hidden={hidden}
        renderAccountLink={renderAccountLink}
      />
    ),
    crypto: (
      <CryptoTradingSummary
        coins={data.coins}
        hidden={hidden}
        renderAccountLink={renderAccountLink}
      />
    ),
  };

  return (
    <div className="grid w-[360px] overflow-hidden rounded-[12px] border border-solid border-v2-border bg-v2-surface-secondary px-5 py-6 shadow-[0px_12px_32px_0px_rgba(0,0,0,0.12)] dark:border-white/10 dark:shadow-[0px_12px_32px_0px_rgba(0,0,0,0.4)]">
      <div
        inert={isSettingsOpen}
        className={cn(
          SCREEN_CLASS,
          'flex flex-col gap-4',
          isSettingsOpen && cn(SCREEN_HIDDEN_CLASS, '-translate-x-6'),
        )}
      >
        <div className="flex flex-col gap-2">
          <h3 className="text-[14px] font-semibold text-v2-text-primary">{t(TITLE_KEY[panel])}</h3>
          <div className="flex flex-col gap-4">
            <BalanceCard
              amount={headline.amount}
              usdValue={headline.usdValue}
              currencyLabel={headline.currencyLabel}
              showCoinIcon={panel !== 'assets'}
              currencySelect={currencySelects[panel]}
              fractionDigits={headline.fractionDigits}
              view={view}
              onViewChange={setView}
              hidden={hidden}
              onOpenSettings={handleOpenSettings}
              settingsButtonRef={settingsButtonRef}
              walletLink={
                <div className="flex justify-center">
                  {renderAccountLink(
                    WALLET_HREF,
                    <span className="flex items-center gap-2 py-2 text-[12px] leading-4 tracking-[-0.12px] text-v2-text-primary">
                      {t('walletOverview')}
                      <Icon name="ArrowSmallRight" size={16} className="text-v2-text-secondary" />
                    </span>,
                  )}
                </div>
              }
            />
            {/* Figma 15073:84517 / 84518: two different glyphs, both grey on dark. */}
            <div className="flex gap-2">
              <Button
                appearance="accent"
                size="M"
                className="flex-1"
                leftIcon={
                  <Icon
                    name="BalanceDeposit"
                    size={20}
                    className="dark:text-v2-icon-primary dark:[button:hover_&]:text-current"
                  />
                }
                onClick={onDeposit}
              >
                {t('deposit')}
              </Button>
              <Button
                appearance="outline"
                size="M"
                className="flex-1"
                leftIcon={<Icon name="BalanceConvert" size={20} className="text-v2-icon-primary" />}
                onClick={onConvert}
              >
                {t('convert')}
              </Button>
            </div>
          </div>
        </div>
        <div
          className={cn(
            'flex flex-col border-t border-solid border-v2-border pt-4',
            TAB_BODY_GAP[tab],
          )}
        >
          {/* Figma tabs: 28px tall, 24px apart, the line in the divider colour. */}
          <UnderlineTabs
            tabs={tabs}
            activeTab={tab}
            onTabChange={handleTabChange}
            className="gap-6 [&>button]:h-7 [&>span]:bg-v2-divider"
          />
          {tabContent[tab]}
        </div>
      </div>
      <div
        inert={!isSettingsOpen}
        className={cn(SCREEN_CLASS, !isSettingsOpen && cn(SCREEN_HIDDEN_CLASS, 'translate-x-6'))}
      >
        <DisplaySettings isActive={isSettingsOpen} onBack={handleCloseSettings} />
      </div>
    </div>
  );
}
